// sendLiveStreamGift.js
'use strict';
import { v4 as uuidv4 } from 'uuid';

import fetch from '../resource/customFetch.js';
import getGiftData from '../selector/getGiftData.js';
import getMeData from '../selector/getMeData.js';
import getIsKaraoke, {
  getIsToyDeviceKaraoke,
} from '../selector/getIsKaraoke.js';
import getIsPrivateShowTicket from '../selector/getIsPrivateShowTicket.js';
import getIsPrivateShowEarlyBirdTicket from '../selector/getIsPrivateShowEarlyBirdTicket.js';
import getLiveStreamData from '../selector/getLiveStreamData.js';
import getGoalData from '../selector/getGoalData.js';
import getTimestampOffset, {
  Accuracy,
} from '../selector/getTimestampOffset.js';
import getPrivateShowStatus from '../selector/getPrivateShowStatus.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';

import addvalue from '../action/addvalue.js';
import pushToastr from '../action/pushToastr.js';
import removeModals from '../action/removeModals.js';
import login from '../action/login.js';
import submitLivestreamPaymentTracking from '../action/submitLivestreamPaymentTracking.js';
import fetchPrivateShowLeaderboard from '../action/fetchPrivateShowLeaderboard.js';
import { queuedToggleModal } from '../action/modalQueueActions.js';

import { EventTypes } from '../resource/trackingConstants.js';
import getResourceUrl from '../resource/getResourceUrl.js';
import { getHeaders } from '../resource/fetchOptionHeader.js';
import handleFetchError from '../resource/handleFetchError.js';
import {
  livestreamPaymentVia,
  CommandProgressHintType,
} from '../resource/liveStreamConstants.js';
import getCurrentUnixTimestamp from '../resource/getCurrentUnixTimestamp.js';
import { Entrypoint } from '../resource/shopConstants.js';
import { privateShowStatus as privateShowStatusConstant } from '../resource/liveStreamConstants.js';
import Delay from '../resource/Delay.js';
import {
  endSentryLogCapture,
  startSentryLogCapture,
} from '../resource/sentry.js';
import { LOG_CAPTURE_LIVESTREAM_SEND_GIFT } from '../resource/sentryLogCaptureNames.js';
import { livestream as livestreamDebug } from '../resource/debug.js';

import {
  MERGE_OPERATION_DATA,
  SET_NETWORKING_FETCHING,
  SET_NETWORKING_SUCCESS,
  SET_NETWORKING_ERROR,
} from '../ActionTypes.js';
import { NEED_AUTH_HANDLER } from '../RemoteConfigKeys.js';

const log = livestreamDebug.extend('log:sendLiveStreamGift');
const errorLog = livestreamDebug.extend('error:sendLiveStreamGift');

const GIFT_STATUS_DISPLAY_MS = 2000;

/**
 * Send live stream gift
 * @kind action
 * @param {string} {streamId} - stream id
 * @param {string} {giftId} - gift id
 * @param {string} {modalId} - the modal id that user click to buy ticket
 * @param {number} {[count = 1]} - gift count
 * @param {boolean} {isHeadMethod} - should use HEAD method to broadcast preload signal or not.
 * @param {boolean} {isTooltip} - whether the gift is sent from tooltip
 * @return {Promise} Action promise.
 */
const sendLiveStreamGift =
  ({
    streamId,
    giftId,
    modalId,
    count = 1,
    isHeadMethod = false,
    isTooltip = false,
  }) =>
  async (dispatch, getState) => {
    const selectPath = ['liveStream', 'gift'];
    const networkingSelectPath = ['liveStream', streamId, 'gift', giftId];

    if (!streamId || !giftId) {
      errorLog('missing streamId or giftId, skip');
      return dispatch({ type: '' });
    }
    startSentryLogCapture({ captureName: LOG_CAPTURE_LIVESTREAM_SEND_GIFT });
    if (isHeadMethod) {
      log(`gift [${giftId}] HEAD start (preload signal)`, {
        streamId,
        giftId,
        url: `/users/${streamId}/gift/${giftId}?count=${count}`,
      });
    } else {
      log(`gift [${giftId}] x${count} POST start`, {
        streamId,
        giftId,
        count,
        url: `/users/${streamId}/gift/${giftId}?count=${count}`,
      });
    }

    const token = getMeData(getState(), 'token');
    if (!token) {
      const needAuthHandler = getRemoteConfigData(
        getState(),
        NEED_AUTH_HANDLER
      );
      errorLog(`gift [${giftId}] blocked: user not authenticated`, {
        streamId,
        giftId,
      });
      endSentryLogCapture({
        captureName: LOG_CAPTURE_LIVESTREAM_SEND_GIFT,
        reason: 'not_authenticated',
      });
      if (needAuthHandler === 'shop') {
        return dispatch(addvalue({ entrypoint: Entrypoint.GIFT, via: 'gift' }));
      } else {
        return dispatch(login({ shouldChooseEntryOption: false }));
      }
    }

    const timestampOffsetSeconds = getTimestampOffset(
      getState(),
      Accuracy.SECOND
    );
    const now = getCurrentUnixTimestamp({
      offsetSeconds: timestampOffsetSeconds,
    });
    const fundingId = getLiveStreamData(
      getState(),
      [streamId, 'privateShow'],
      'fundingId'
    );
    const performingId = getLiveStreamData(
      getState(),
      [streamId, 'privateShow'],
      'performingId'
    );
    const fundingExpireUnixTimestamp = getGoalData(
      getState(),
      [fundingId],
      'exp'
    );
    const privateShowStatus = getPrivateShowStatus(getState(), streamId);

    const isKaraoke = getIsKaraoke(getState(), streamId, giftId);
    const isToyDeviceKaraoke = getIsToyDeviceKaraoke(
      getState(),
      streamId,
      giftId
    );
    const isEarlyBirdTicket = getIsPrivateShowEarlyBirdTicket(
      getState(),
      giftId,
      fundingId
    );
    const isShowTicket = getIsPrivateShowTicket(
      getState(),
      giftId,
      fundingId,
      performingId
    );
    const isTicket = isEarlyBirdTicket || isShowTicket;
    const requestId = uuidv4();

    dispatch(
      updateGiftSendingStatus({
        selectPath,
        giftSendingStatus: 'sending', // sending, success, error
        isHeadMethod,
      })
    );

    if (
      isTicket &&
      now >= fundingExpireUnixTimestamp &&
      [
        privateShowStatusConstant.FUNDING,
        privateShowStatusConstant.FUNDING_FINAL_CALL,
      ].includes(privateShowStatus)
    ) {
      dispatch(
        updateGiftSendingStatus({
          selectPath,
          giftSendingStatus: null, // sending, success, error
          isHeadMethod,
        })
      );
      endSentryLogCapture({
        captureName: LOG_CAPTURE_LIVESTREAM_SEND_GIFT,
        reason: 'funding_expired',
      });
      return dispatch(
        pushToastr({ textKey: 'buy_ticket_failed_toast', color: 'error' })
      );
    }

    if (isToyDeviceKaraoke) {
      const isToyDeviceOnline = getLiveStreamData(
        getState(),
        [streamId],
        'device'
      );
      if (!isToyDeviceOnline) {
        dispatch(
          updateGiftSendingStatus({
            selectPath,
            giftSendingStatus: null, // sending, success, error
            isHeadMethod,
          })
        );
        endSentryLogCapture({
          captureName: LOG_CAPTURE_LIVESTREAM_SEND_GIFT,
          reason: 'toy_device_offline',
        });
        return dispatch(
          pushToastr({ textKey: 'lovense_is_not_connected', color: 'error' })
        );
      }
    }
    if (isTicket) {
      const price = getGiftData(getState(), giftId, 'price');

      // no need to track if is head method
      if (!isHeadMethod) {
        dispatch(
          submitLivestreamPaymentTracking({
            trackingType: EventTypes.LIVESTREAM_PAYMENT,
            streamId,
            sessionId: getLiveStreamData(getState(), [streamId], 'sessionId'),
            requestId,
            via: livestreamPaymentVia.CLICK,
            price,
            giftId,
            count,
            modalId,
            duration: getGoalData(
              getState(),
              [fundingId, 'context'],
              'perform_duration'
            ),
          })
        );
      }
    }
    const fetchOptions = {
      method: isHeadMethod ? 'HEAD' : 'POST',
      headers: {
        ...getHeaders(),
        Authorization: `Bearer ${token}`,
        'X-Request-ID': requestId,
      },
    };

    const url = getResourceUrl({
      endpoint: `/users/${streamId}/gift/${giftId}?count=${count}`,
    });

    dispatch({
      type: SET_NETWORKING_FETCHING,
      payload: { selectPath: networkingSelectPath },
    });
    try {
      let response = await fetch(url.href, fetchOptions);

      if (!response.ok) {
        response = await handleFetchError({
          response,
          dispatch,
          getState,
          fetchOptions,
          fetchUrl: url,
        });
      }

      if (isKaraoke || isTicket || isTooltip) {
        if (isTicket) {
          const meId = getMeData(getState(), 'id');
          if (meId) {
            dispatch(
              fetchPrivateShowLeaderboard({
                streamId,
                fundingId,
                userIds: [meId],
                unixTimestamp: getCurrentUnixTimestamp({
                  offsetSeconds: timestampOffsetSeconds,
                }),
              })
            );
          }
          dispatch(
            removeModals({
              ids: ['PrivateShowBuyTicketModal', 'PrivateShowUnlockModal'],
              transitionStatus: 'closing',
            })
          );
          dispatch(
            pushToastr({
              textKey: 'ticket_purchase_successful',
              color: 'success',
            })
          );
          dispatch(
            updateGiftSendingStatus({
              selectPath,
              giftSendingStatus: 'success',
              isHeadMethod,
            })
          );
        }

        if (isKaraoke && !isHeadMethod) {
          dispatch(
            queuedToggleModal({
              modalId: 'LivestreamCommandProgressModal',
              payload: {
                hintType: CommandProgressHintType.COMMAND_SENT,
              },
              isOpen: true,
              modalOption: { closeAfterSeconds: 3 },
            })
          );
        }

        await new Delay(600).promise();

        dispatch(
          updateGiftSendingStatus({
            selectPath,
            giftSendingStatus: null,
            isHeadMethod,
          })
        );
      } else {
        dispatch(
          updateGiftSendingStatus({
            selectPath,
            giftSendingStatus: 'success',
            isHeadMethod,
          })
        );
        setTimeout(() => {
          dispatch(
            updateGiftSendingStatus({
              selectPath,
              giftSendingStatus: null,
              isHeadMethod,
            })
          );
        }, GIFT_STATUS_DISPLAY_MS);
      }
      if (!isHeadMethod) {
        log(`gift [${giftId}] x${count} POST success`, { streamId, giftId });
      }
      return dispatch({
        type: SET_NETWORKING_SUCCESS,
        payload: { selectPath: networkingSelectPath },
      });
    } catch (error) {
      if (isKaraoke || isTicket || isTooltip) {
        dispatch(
          updateGiftSendingStatus({
            selectPath,
            giftSendingStatus: null,
            isHeadMethod,
          })
        );
      } else {
        dispatch(
          updateGiftSendingStatus({
            selectPath,
            giftSendingStatus: 'error',
            isHeadMethod,
          })
        );
        setTimeout(() => {
          dispatch(
            updateGiftSendingStatus({
              selectPath,
              giftSendingStatus: null,
              isHeadMethod,
            })
          );
        }, GIFT_STATUS_DISPLAY_MS);
      }
      errorLog(
        `gift [${giftId}] ${isHeadMethod ? 'HEAD' : 'POST'} failed: ${error.status || ''} ${error.message || ''}`.trim(),
        {
          streamId,
          giftId,
          status: error.status,
          errorCode: error.code,
          errorMessage: error.message,
        }
      );
      endSentryLogCapture({
        captureName: LOG_CAPTURE_LIVESTREAM_SEND_GIFT,
        reason: error.message,
      });
      return dispatch({
        type: SET_NETWORKING_ERROR,
        payload: { selectPath: networkingSelectPath, error },
      });
    }
  };

export default sendLiveStreamGift;

/**
 * Update gift sending status
 * @kind action
 * @param {Array<string>} {selectPath} - select path.
 * @param {'sending'|'success'|'error'|null} {giftSendingStatus} - gift sending status.
 * @param {boolean} {isHeadMethod} - should use HEAD method to broadcast preload signal or not.
 * @return {Promise} Action promise.
 */
const updateGiftSendingStatus =
  ({ selectPath, giftSendingStatus, isHeadMethod } = {}) =>
  async dispatch => {
    if (!isHeadMethod) {
      dispatch({
        type: MERGE_OPERATION_DATA,
        payload: {
          selectPath,
          data: {
            giftSendingStatus,
          },
        },
      });
    }
  };
